import i18n from '../../utils/i18n'

type PriceOptions = {
  currency?: string
  minimumFractionDigits?: number
}

/**
 * Formats a price value as a currency with the correct format and symbol.
 *
 * @param {number | null | undefined} price - The price value to format
 * @param {PriceOptions | null} [options] - Optional configuration for price formatting
 * @param {SupportedCurrency} [options.currency] - Currency code (e.g., 'EUR', 'USD')
 * @param {number} [options.minimumFractionDigits=2] - Minimum number of decimal places
 *
 * @returns {JSX.Element | null} Formatted price as JSX element or null if price is null/undefined
 *
 * @example
 * // Returns "€123.45" (depending on language settings)
 * PriceBodyTemplate(123.45, { currency: 'EUR' })
 *
 * @example
 * // Returns "$123.45" (depending on language settings)
 * PriceBodyTemplate(123.45, { currency: 'USD', minimumFractionDigits: 2 })
 */

export const PriceBodyTemplate = (
  price: number | null | undefined,
  options?: PriceOptions | null,
) => {
  if (price === null || price === undefined) return null

  const { language } = i18n

  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: options?.minimumFractionDigits || 2,
  }

  if (options?.currency) {
    formatOptions.style = 'currency'
    formatOptions.currency = options?.currency
  }

  return <>{price.toLocaleString(language, formatOptions)}</>
}
